import AnimationFrame from './animation-frame.class';
import wait from './wait';

/**
 * Class for tweaking objects
 *   - Object to tweak should be passed in by `from()` method
 *   - Object to tweak to should be passed in by `to()` method
 *
 * Only properties which match (are both in from and to) will be tweaked
 *
 * Only properties up to depth one will be tweaked
 *
 * @class Tweak
 */
class Tweak {
  constructor() {
    // Copy of the _from object
    this._original = {};

    // Properties
    this._from = null;
    this._to = null;
    this._duration = null;
    this._delay = 0;

    // Animation frame
    this._startTime = null;
    this._animationFrame = new AnimationFrame(this._onAnimationFrameTick.bind(this));

    // Events
    this._onTick = null;
    this._onStart = null;
    this._onEnd = null;
  }

  /**
   * This function is called on every animation frame
   *
   * @private
   */
  _onAnimationFrameTick() {
    const delta = (Date.now() - this._startTime) / this._duration;

    Object.keys(this._to).forEach((key) => {
      if (this._from.hasOwnProperty(key)) {
        this._from[key] =
          this._original[key] + (this._to[key] - this._original[key]) * Math.min(1, delta);
      }
    });

    // Trigger onTick
    if (this._onTick instanceof Function) {
      this._onTick(this._from, delta);
    }

    if (delta > 1) {
      // Trigger onEnd
      if (this._onEnd instanceof Function) {
        this._onEnd(this._from);
      }

      this._animationFrame.stop();
    }
  }

  /**
   * Start the tweaking process
   *
   * @returns {Tweak}
   */
  start() {
    function _start() {
      this._startTime = Date.now();
      this._animationFrame.start();

      // Trigger onStart
      if (this._onStart instanceof Function) {
        this._onStart();
      }
    }

    if (this._delay > 0) {
      wait(this._delay).then(() => _start.call(this));
    } else {
      _start.call(this);
    }

    return this;
  }

  /**
   * Sets the `from` property
   *
   * @param {Object} f
   */
  from(f) {
    this._from = f;

    this._original = {};
    Object.entries(f).forEach(([key, value]) => (this._original[key] = value));

    return this;
  }

  /**
   * Sets the `to` property
   *
   * @param {Object} t
   */
  to(t) {
    this._to = t;
    return this;
  }

  /**
   * Sets the `duration` property
   *
   * @param {Number} d
   */
  duration(d) {
    this._duration = d;
    return this;
  }

  /**
   * Sets the `delay` property
   *
   * @param {Number} d
   */
  delay(d) {
    this._delay = d;
    return this;
  }

  /**
   * Binds a function to the `onStart` event
   *
   * @param {Function} fn
   */
  onStart(fn) {
    this._onStart = fn;
    return this;
  }

  /**
   * Binds a function to the `onEnd` event
   *
   * @param {Function} fn
   */
  onEnd(fn) {
    this._onEnd = fn;
    return this;
  }

  /**
   * Binds a function to the `onTick` event
   *
   * @param {Function} fn
   */
  onTick(fn) {
    this._onTick = fn;
    return this;
  }
}

export default Tweak;
